/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AccountType, FeedResource, FeedType, PackageReference } from "@octopusdeploy/octopus-server-client";
import { ProcessType } from "@octopusdeploy/octopus-server-client";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
import type { ProcessTemplateActionInputsDependencies } from "~/components/ActionInputs/ProcessTemplateActionInputs";
import { createProcessTemplateInitialInputsBuilder, ProcessTemplateActionInputs } from "~/components/ActionInputs/ProcessTemplateActionInputs";
import type { ProjectActionInputsDependencies } from "~/components/ActionInputs/ProjectActionInputs";
import { createProjectInitialInputsBuilder, ProjectActionInputs } from "~/components/ActionInputs/ProjectActionInputs";
import type { ProjectTemplateActionInputsDependencies } from "~/components/ActionInputs/ProjectTemplateActionInputs";
import { createProjectTemplateInitialInputsBuilder, ProjectTemplateActionInputs } from "~/components/ActionInputs/ProjectTemplateActionInputs";
import type { CombinedVolumeDetails } from "~/components/Actions/kubernetes/kubernetesDeployContainersAction";
import type { ActionTemplateActionInputsDependencies } from "./ActionTemplateActionInputs";
import { ActionTemplateActionInputs } from "./ActionTemplateActionInputs";
type ActionInputElement = {
    type: "docker-reference-list-form";
    key: string;
    hasModelDirty?: boolean;
    hasRelatedTriggersDependencies?: boolean;
    hideAcquisitionChoices: boolean;
} | {
    type: "container-image-reference-list";
    key: string;
    hasModelDirty?: boolean;
    hasRelatedTriggersDependencies?: boolean;
    hideAcquisitionChoices: boolean;
} | {
    type: "deferred-package-selector";
    key: string;
    feedType?: FeedTypeOrFactory;
    hasModelDirty?: boolean;
    hasRelatedTriggersDependencies?: boolean;
} | {
    type: "package-selector";
    key: string;
    feedType?: FeedTypeOrFactory;
    hasModelDirty?: boolean;
    hasRelatedTriggersDependencies?: boolean;
} | {
    type: "aws-account-variable-selector";
    key: string;
} | {
    type: "google-account-variable-selector";
    key: string;
} | {
    type: "azure-account-selector";
    key: string;
    accountType: AccountType[];
    clearLegacyProps?: () => void;
    propertyKey: string;
    noDropdown?: boolean;
} | {
    type: "sensitive-value-selector";
    key: string;
    propertyKey: string;
    label?: string;
    helperText?: React.ReactNode;
} | {
    type: "container-list-section";
    key: string;
    volumes: CombinedVolumeDetails[];
} | {
    type: "certificate-selector";
    key: string;
    propertyKey: string;
};
export type FeedTypeOrFactory = FeedType[] | ((actionInputsDependencies: ActionInputsDependencies) => FeedType[]);
export type ActionInputDefinition = ActionInputElement[];
interface ActionInputsProps {
    actionUIDefinition: ActionInputDefinition;
    dependencies: ActionInputsDependencies;
}
export interface InitialInputsBuilder {
    initializePackages: () => PackageReference<any>[];
}
export type ActionInputsDependencies = ProjectActionInputsDependencies | ProcessTemplateActionInputsDependencies | ActionTemplateActionInputsDependencies | ProjectTemplateActionInputsDependencies;
export function isProjectOrProcessTemplateActionInput(actionInputsDependencies: ActionInputsDependencies): actionInputsDependencies is ProcessTemplateActionInputsDependencies | ProjectTemplateActionInputsDependencies {
    return actionInputsDependencies.type === "project-template" || actionInputsDependencies.type === "process-template";
}
export function ActionInputs({ actionUIDefinition, dependencies }: ActionInputsProps) {
    const type = dependencies.type;
    switch (type) {
        case "project":
            return <ProjectActionInputs actionUIDefinition={actionUIDefinition} dependencies={dependencies}/>;
        case "action-template":
            return <ActionTemplateActionInputs actionUIDefinition={actionUIDefinition} dependencies={dependencies}/>;
        case "process-template":
            return <ProcessTemplateActionInputs actionUIDefinition={actionUIDefinition} dependencies={dependencies}/>;
        case "project-template":
            return <ProjectTemplateActionInputs actionUIDefinition={actionUIDefinition} dependencies={dependencies}/>;
        default:
            exhaustiveCheck(type, "Unknown action inputs dependencies type");
    }
}
export function createProcessInitialInputsBuilder(processType: ProcessType, feeds: FeedResource[]): InitialInputsBuilder {
    switch (processType) {
        case ProcessType.ProcessTemplate:
            return createProcessTemplateInitialInputsBuilder();
        case ProcessType.ProjectTemplate:
            return createProjectTemplateInitialInputsBuilder();
        case ProcessType.Deployment:
        case ProcessType.Runbook:
        case ProcessType.TemplatedProject:
            return createProjectInitialInputsBuilder(feeds);
        default:
            exhaustiveCheck(processType, "Unknown process type");
    }
}
